import React, { useState, useEffect } from "react";
import LifeSaverLayout from "../../components/LifeSaverLayout";
import api, { baseURL } from "../../services/api";
import { FiMessageSquare, FiVideo, FiPlay } from "react-icons/fi";

const Dashboard = () => {
  const [data, setData] = useState({
    communications: [],
    videos: [],
    statuses: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [commRes, videosRes, statusRes] = await Promise.all([
        api.get("/communications/my"),
        api.get("/videos/my"),
        api.get("/dashboard-status"),
      ]);

      setData({
        communications: commRes.data.slice(0, 3), // Latest 3
        videos: videosRes.data.slice(0, 4), // Latest 4
        statuses: statusRes.data,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getYouTubeEmbedUrl = (url) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11
      ? `https://www.youtube.com/embed/${match[2]}`
      : url;
  };

  if (loading) {
    return (
      <LifeSaverLayout>
        <div className="spinner"></div>
      </LifeSaverLayout>
    );
  }

  return (
    <LifeSaverLayout>
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
      </div>

      {/* Status Table */}
      <div className="card" style={{ marginBottom: "2rem" }}>
        <div className="card-header">
          <h2 className="card-title">Status</h2>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Project</th>
                <th>Status</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {data.statuses.map((status) => (
                <tr key={status.id}>
                  <td>{status.serial_number}</td>
                  <td>{status.exchange}</td>
                  <td>
                    <span
                      className={`badge ${
                        status.status.toLowerCase() === "active"
                          ? "badge-success"
                          : status.status.toLowerCase() === "inactive"
                          ? "badge-danger"
                          : "badge-warning"
                      }`}
                    >
                      {status.status}
                    </span>
                  </td>
                  <td>{status.remarks || "-"}</td>
                </tr>
              ))}
              {data.statuses.length === 0 && (
                <tr>
                  <td colSpan="4" className="text-center">
                    No status entries available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Communications */}
      <div className="card" style={{ marginBottom: "2rem" }}>
        <div className="card-header">
          <h2 className="card-title flex items-center">
            <FiMessageSquare style={{ marginRight: "0.5rem" }} />
            Recent Communications
          </h2>
          <a href="/member/communications" className="btn btn-secondary">
            View All
          </a>
        </div>

        {data.communications.length > 0 ? (
          data.communications.map((comm) => (
            <div key={comm.id} className="communication-card">
              <div className="communication-date">
                {formatDate(comm.message_date)}
              </div>
              <div className="communication-title">
                {comm.title || "Announcement"}
              </div>
              {comm.message && (
                <div className="communication-message">{comm.message}</div>
              )}
              {comm.voice_file_path && (
                <div className="voice-player">
                  <audio controls style={{ width: "100%" }}>
                    <source src={`${baseURL}${comm.voice_file_path}`} />
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}
            </div>
          ))
        ) : (
          <p style={{ color: "#666" }}>No communications available</p>
        )}
      </div>

      {/* Videos Section */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title flex items-center">
            <FiVideo style={{ marginRight: "0.5rem" }} />
            Videos
          </h2>
        </div>

        {data.videos.length > 0 ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "1rem",
            }}
          >
            {data.videos.map((video) => (
              <div key={video.id} className="video-card">
                <div className="video-thumbnail">
                  {video.video_url.includes("youtube") ? (
                    <iframe
                      width="100%"
                      height="180"
                      src={getYouTubeEmbedUrl(video.video_url)}
                      title={video.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  ) : (
                    <a
                      href={video.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FiPlay size={48} />
                    </a>
                  )}
                </div>
                <div className="video-info">
                  <div className="video-title">{video.title}</div>
                  {video.description && (
                    <div className="video-description">{video.description}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: "#666" }}>No videos available</p>
        )}
      </div>
    </LifeSaverLayout>
  );
};

export default Dashboard;
